import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  InternalAdminDistributionGroupFragment,
  InternalAdminDistributionGroupQuery,
  InternalAdminDistributionGroupQueryVariables,
  InternalAdminUserQuery,
  InternalAdminUserQueryVariables,
} from "gql/graphql";
import moment from "moment";
import React, { memo, useCallback, useRef } from "react";
import colors from "app/colors";
import { MONTH_DAY_YEAR_FORMAT } from "modules/utils/Constants";
import {
  HashIcon,
  AtSignIcon,
  InfoIcon,
  ExternalIcon,
  GroupMembershipIcon,
  PencilOutlineIcon,
} from "modules/icons";
import INTERNAL_ADMIN_DISTRIBUTION_GROUP_QUERY from "../_networking/internal-admin-distribution-group";
import DistributionGroupTypeIcon from "modules/admin/distribution-group-type-icon";
import { isEmpty } from "lodash";
import InternalAdminDistributionGroupMembershipCriteria from "../distribution-groups/internal-admin-distribution-group-membership-criteria";
import InternalAdminDistributionGroupHistoricalAccess from "../distribution-groups/internal-admin-distribution-group-historical-access";
import InternalAdminDistributionGroupMemberList from "../distribution-groups/internal-admin-distribution-group-member-list";
import INTERNAL_ADMIN_USER_QUERY from "../_networking/internal-admin-user";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import InternalAdminDistributionGroupArchiveButton from "../buttons/internal-admin-distribution-group-archive-button";

interface IProps {
  distributionGroupId: string;
}

const InternalAdminDistributionGroupDrawer = ({
  distributionGroupId,
}: IProps) => {
  const { openDrawer } = useOverlay();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, refetch } = useQuery<
    InternalAdminDistributionGroupQuery,
    InternalAdminDistributionGroupQueryVariables
  >(INTERNAL_ADMIN_DISTRIBUTION_GROUP_QUERY, {
    fetchPolicy: "network-only",
    variables: {
      id: distributionGroupId,
    },
  });

  const distributionGroup: InternalAdminDistributionGroupFragment | undefined =
    data && data.internalAdminDistributionGroup.length === 1
      ? data.internalAdminDistributionGroup[0]
      : undefined;

  const { data: userData } = useQuery<
    InternalAdminUserQuery,
    InternalAdminUserQueryVariables
  >(INTERNAL_ADMIN_USER_QUERY, {
    fetchPolicy: "network-only",
    variables:
      distributionGroup && distributionGroup.userIdList.length > 0
        ? {
            id: distributionGroup.userIdList,
          }
        : undefined,
    skip: !distributionGroup || distributionGroup.userIdList.length === 0,
  });

  const users = userData ? userData.internalAdminUser : [];

  const { data: emailUserData } = useQuery<
    InternalAdminUserQuery,
    InternalAdminUserQueryVariables
  >(INTERNAL_ADMIN_USER_QUERY, {
    fetchPolicy: "network-only",
    variables:
      distributionGroup && distributionGroup.emailUserIdList.length > 0
        ? {
            id: distributionGroup.emailUserIdList,
          }
        : undefined,
    skip: !distributionGroup || distributionGroup.userIdList.length === 0,
  });

  const emailUsers = emailUserData ? emailUserData.internalAdminUser : [];

  const onEditClicked = useCallback(() => {
    openDrawer({
      type: DRAWER_TYPE.INTERNAL_ADMIN_ADD_EDIT_DISTRIBUTION_GROUP,
      id: distributionGroupId,
      onCloseCallback: () =>
        openDrawer({
          type: DRAWER_TYPE.INTERNAL_ADMIN_DISTRIBUTION_GROUP,
          id: distributionGroupId,
        }),
    });
  }, [distributionGroupId, openDrawer]);

  if (!distributionGroup) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton top={5} left={5} color={colors.gray30} />
      <DrawerHeader>
        <Center fontWeight={700} fontFamily="Outfit" fontSize={26}>
          Group Details
        </Center>
      </DrawerHeader>
      <DrawerBody
        py={0}
        px={8}
        w="full"
        h="full"
        ref={scrollRef}
        flex={1}
        overflowY="auto"
      >
        <VStack w="full" alignItems="flex-start" flex={1} spacing={8}>
          <HStack w="full" spacing={2}>
            <Text
              fontSize={24}
              fontWeight={700}
              fontStyle={distributionGroup.active ? "normal" : "italic"}
              color={
                distributionGroup.active
                  ? colors.blackSecondary
                  : colors.grayMain
              }
            >
              <AtSignIcon
                boxSize={8}
                fill={
                  distributionGroup.active
                    ? colors.blackSecondary
                    : colors.grayMain
                }
              />
              {distributionGroup.name}
            </Text>
            <DistributionGroupTypeIcon
              distributionGroup={distributionGroup}
              iconProps={{ boxSize: 8, fill: colors.blackSecondary }}
            />
            {!!distributionGroup.emailUserIdList.length && (
              <Box pl={2}>
                <ExternalIcon fill={colors.transparent} />
              </Box>
            )}
          </HStack>

          <VStack w="full" alignItems="flex-start" spacing={3}>
            <HStack
              w="full"
              borderBottomColor={colors.black}
              borderBottomWidth={1}
            >
              <InfoIcon boxSize={6} />
              <Text fontWeight={600} fontSize={18}>
                Group Info
              </Text>
              <Spacer />
              {distributionGroup.active && (
                <Button
                  variant="ghost"
                  size="xs"
                  color={colors.orangePrimary}
                  fontSize={12}
                  fontWeight={500}
                  onClick={onEditClicked}
                  leftIcon={<PencilOutlineIcon fill={colors.orangePrimary} />}
                  h={6}
                >
                  Edit
                </Button>
              )}
            </HStack>
            {distributionGroup.tagList.length > 0 && (
              <>
                <VStack w="full" alignItems="flex-start" spacing={1}>
                  <Text fontWeight={700} fontSize={16}>
                    Tag(s)
                  </Text>
                  <Wrap>
                    {distributionGroup.tagList.map((tag) => (
                      <WrapItem key={tag.id}>
                        <HStack
                          spacing={1}
                          paddingX={2}
                          borderRadius={16}
                          borderWidth={1}
                          fontWeight={700}
                          fontSize={13}
                          borderColor={colors.grayPrimary}
                          color={colors.blue}
                        >
                          <HashIcon boxSize="11px" fill={colors.gray50} />
                          <Text
                            color={colors.gray50}
                          >{` ${tag.displayName}`}</Text>
                        </HStack>
                      </WrapItem>
                    ))}
                  </Wrap>
                </VStack>
                <Divider borderBottomColor={colors.gray0} />
              </>
            )}
            {!isEmpty(distributionGroup.description) && (
              <>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <Text fontWeight={700} fontSize={15}>
                    Description
                  </Text>
                  <Text fontSize={14} color={colors.gray50}>
                    {distributionGroup.description}
                  </Text>
                </VStack>
                <Divider borderBottomColor={colors.gray0} />
              </>
            )}
            <HStack w="full">
              <Text
                fontSize={14}
                fontWeight={600}
                color={colors.blackSecondary}
              >
                Date Created
              </Text>
              <Spacer />
              <Text fontSize={14} fontWeight={400} color={colors.gray50}>
                {moment(distributionGroup.createTime).format(
                  MONTH_DAY_YEAR_FORMAT,
                )}
              </Text>
            </HStack>
            <Divider borderBottomColor={colors.gray0} />
            {distributionGroup.active === false &&
              distributionGroup.inactiveTime !== null && (
                <HStack w="full" pt={4}>
                  <Text fontSize={14} fontWeight={600}>
                    Archived
                  </Text>
                  <Spacer />
                  <Text fontSize={14} color={colors.gray50}>
                    {moment(distributionGroup.inactiveTime).format(
                      MONTH_DAY_YEAR_FORMAT,
                    )}
                  </Text>
                </HStack>
              )}
            <InternalAdminDistributionGroupArchiveButton
              distributionGroup={distributionGroup}
              refetch={refetch}
            />
          </VStack>

          <VStack w="full" alignItems="flex-start" spacing={4}>
            <HStack
              w="full"
              borderBottomColor={colors.black}
              borderBottomWidth={1}
            >
              <GroupMembershipIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontWeight={600} fontSize={18}>
                Membership
              </Text>
            </HStack>
            <InternalAdminDistributionGroupMembershipCriteria
              distributionGroup={distributionGroup}
            />
            <InternalAdminDistributionGroupHistoricalAccess
              distributionGroup={distributionGroup}
            />
          </VStack>
        </VStack>
        <InternalAdminDistributionGroupMemberList
          customScrollParent={scrollRef}
          distributionGroup={distributionGroup}
          users={users}
          emailUsers={emailUsers}
        />
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(InternalAdminDistributionGroupDrawer);
